/**
 * The way into the headset, and the bridge back out of it.
 *
 * Everything inside the session runs imperatively at the headset's refresh rate, so React's
 * job here is only to start it, keep a view object up to date for it to read, and turn what it
 * fires back into the same actions the page's own buttons send.
 */
import { CongestionControl, Priority, Reliability, json, useCtrlSession, useOrg, useSession, useSubscription,
         useTimeSync } from "adamo-react";
import { createSharedDecodedStream } from "adamo/media";
import { type MutableRefObject, useCallback, useEffect, useRef, useState } from "react";

import type { Sender } from "../adamo/actions";
import type { BoxMap, ToolModel } from "../adamo/boxmap";
import type { StatusFeed } from "../adamo/status";
import { handTopic, hapticTopic, perfTopic, videoTopic } from "../adamo/topics";
import type { Driver, HandFrame, Haptic, PathState, Settings } from "../adamo/types";
import { operatorId } from "../state/operatorId";
import { LinkMeter } from "./meter";
import { VrScene } from "./scene";

const REPORT_MS = 500;   // how often the headset tells the arm what it measured

/** How this browser likes to be shown the picture. Nothing here concerns the robot. */
type Local = { convergence: number; swapEyes: boolean; flatten: boolean; verticalOffset: number;
               /** The box map, as a small model beside the picture. */
               showMap: boolean };

const LOCAL_KEY = "operator.view";
const LOCAL_DEFAULTS: Local = { convergence: 0, swapEyes: false, flatten: false, verticalOffset: 0, showMap: true };

function loadLocal(): Local {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (raw) return { ...LOCAL_DEFAULTS, ...JSON.parse(raw) };
  } catch {
    // a private window keeps its own defaults, which is fine
  }
  return { ...LOCAL_DEFAULTS };
}

export function VrButton({ robot, track, org, operator, feed, sender, settings, change, path, driver,
                           onClaim, settingsUnderstood, boxMap, toolModel }: {
  robot: string;
  /** The track the headset shows, and whether its frame is a side-by-side pair. */
  track: { name: string; stereo: boolean } | null;
  org: string | null;
  operator: string;
  feed: StatusFeed;
  sender: Sender;
  settings: Settings;
  change: <K extends keyof Settings>(key: K, value: Settings[K], final?: boolean) => void;
  path: PathState;
  driver: Driver;
  onClaim: () => void;
  settingsUnderstood: boolean;
  /** The newest box map, read by the headset's render loop. */
  boxMap?: MutableRefObject<BoxMap | null>;
  /** ... and the gripper end's meshes, once they have come. */
  toolModel?: MutableRefObject<ToolModel | null>;
}) {
  const session = useSession();
  const ctrl = useCtrlSession();
  const resolvedOrg = useOrg() ?? org;
  const [supported, setSupported] = useState<boolean | null>(null);
  const [running, setRunning] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const scene = useRef<VrScene | null>(null);
  const local = useRef<Local>(loadLocal());
  // The relay's shared clock, which the arm reads too: the one thing that makes a one-way latency
  // mean anything. Read through a ref so a session that syncs later is picked up mid-flight.
  const timeSync = useTimeSync();
  const clock = useRef(timeSync);
  clock.current = timeSync;

  // The scene reads this every frame rather than being re-rendered.
  const snapshot = () => ({ settings, path, driver, status: feed.current(),
                            sourceIsStereo: Boolean(track?.stereo), settingsUnderstood,
                            ...local.current });
  const view = useRef(snapshot());
  view.current = snapshot();

  useEffect(() => {
    navigator.xr?.isSessionSupported("immersive-vr").then(setSupported).catch(() => setSupported(false));
    if (!navigator.xr) setSupported(false);
  }, []);

  useSubscription(resolvedOrg ? hapticTopic(resolvedOrg, robot) : "", (sample) => {
    const pulse = json.decode<Haptic>(sample.payload().toBytes());
    scene.current?.pulseHand(pulse.hand, pulse.intensity, pulse.ms);
  }, { enabled: Boolean(resolvedOrg) && running });

  const handPublisher = useRef<Record<string, { put: (b: Uint8Array) => Promise<void> } | null>>({});

  const start = useCallback(async () => {
    if (!session || !ctrl || !resolvedOrg || !track) return;
    setProblem(null);
    try {
      // Passthrough when the headset offers it, as the original page does: seeing the room
      // around the screen is what makes a session at a desk bearable.
      const xr = navigator.xr!;
      const immersive = (await xr.isSessionSupported("immersive-ar").catch(() => false))
        ? "immersive-ar" : "immersive-vr";
      const xrSession = await xr.requestSession(immersive as XRSessionMode, { requiredFeatures: ["local-floor"] });

      for (const side of ["left", "right"] as const) {
        handPublisher.current[side] = await ctrl.declarePublisher(handTopic(resolvedOrg, operator, side), {
          priority: 1, express: true,
        });
      }
      // Best effort, and below the controls. It shares the controls' socket, and a reliable put
      // queued on a link that has dropped holds everything behind it until it goes -- the SDK's
      // own clock pings are best effort for exactly that reason. A lost report costs nothing: its
      // counters are cumulative and its events ride along in the next ten.
      const perfPublisher = await ctrl.declarePublisher(perfTopic(resolvedOrg, operator), {
        priority: Priority.DATA, congestionControl: CongestionControl.DROP, reliability: Reliability.BEST_EFFORT,
      });
      const meter = new LinkMeter(() => clock.current);
      meter.track = track.name;

      const built = new VrScene({
        view: () => view.current,
        onHands: (hands) => {
          const stamp = Date.now() / 1000;
          const sentUs = meter.fabricNowUs();
          for (const [side, hand] of Object.entries(hands)) {
            const seq = meter.nextSeq(side as "left" | "right");
            const frame: HandFrame = { ...hand!, stamp, operator_id: operatorId(), seq, sent_us: sentUs };
            handPublisher.current[side]?.put(json.encode(frame)).catch(() => undefined);
          }
        },
        latestStatus: () => ({ status: feed.current(), arrivedAt: performance.now() - feed.age() * 1000 }),
        latestBoxMap: () => boxMap?.current ?? null,
        latestToolModel: () => toolModel?.current ?? null,
        onAction: (name) => (name === "take_over" ? onClaim() : sender.send(name)),
        onSetting: (key, value, final) => change(key, value, final),
        onLocal: (patch) => {
          local.current = { ...local.current, ...patch };
          try { localStorage.setItem(LOCAL_KEY, JSON.stringify(local.current)); } catch { /* fine */ }
        },
        onEnd: () => { setRunning(false); scene.current = null; },
      }, meter, operatorId());
      document.body.appendChild(built.canvas);
      built.canvas.style.display = "none";
      scene.current = built;
      built.setView(local.current);
      await built.start(xrSession, track.stereo);
      setRunning(true);
      onClaim();

      const stream = await createSharedDecodedStream({
        session: session as never,
        topic: videoTopic(resolvedOrg, robot, track.name),
        timeSync: clock.current ?? undefined,
        onError: (err: Error) => setProblem(`video: ${err.message}`),
      });
      const stop = stream.subscribe((frame) => built.sink.push(frame));
      const unwatch = [
        stream.observePackets((packet) => {
          if (packet.captureUnixUs !== undefined && packet.captureUnixUs > 0n) meter.captureStamped = true;
        }),
        stream.observeFrameDrops(() => meter.videoDrop()),
      ];
      const reporting = window.setInterval(() => {
        perfPublisher.put(json.encode(meter.report(operatorId()))).catch(() => undefined);
      }, REPORT_MS);
      xrSession.addEventListener("end", () => {
        window.clearInterval(reporting);
        unwatch.forEach((off) => off());
        void (perfPublisher as { undeclare?: () => Promise<void> }).undeclare?.().catch(() => undefined);
        stop(); stream.close(); built.canvas.remove();
      });
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
      setRunning(false);
    }
  }, [session, ctrl, resolvedOrg, track, robot, operator, sender, change, onClaim, feed]);

  const ready = Boolean(session && ctrl && resolvedOrg && track) && supported === true;
  return (
    <>
      <button className="primary start-vr" disabled={!ready || running} onClick={() => void start()}>
        {running ? "In VR" : "Start VR"}
      </button>
      <p className="note" style={{ marginTop: 8 }}>
        {supported === false && "This browser has no VR headset. Open the portal in the headset's own browser."}
        {supported === true && !track && "No camera is streaming, so there is nothing to show in VR."}
        {supported === true && track && !running && (track.stereo
          ? `Stereo: “${track.name}”, one eye each. Gear menu on the right.`
          : `“${track.name}” is not a stereo track, so it is shown flat to both eyes. `
            + `The arm has to publish a side-by-side pair for a 3D view.`)}
        {problem && ` — ${problem}`}
      </p>
    </>
  );
}
